import AdaptationPlanningIcon from '@app/domains/useCases/components/AdaptationPlanningIcon'
import type { UseCaseConfig } from '../types'

export const adaptationPlanning: UseCaseConfig = {
  id: 'adaptation-planning',
  title: 'Adaptation Planning',
  tagline: 'Guide communities through changing climate risk.',
  accentColor: 'deepTeal',
  iconComponent: AdaptationPlanningIcon,
  roles: [
    'Climate change advisors and adaptation planning leads',
    'Strategic planning and infrastructure teams',
    'Government agencies and Crown entities',
    'Iwi and hapū',
    'Adaptation planning consultancies',
  ],
  keyQuestions: [
    'How might changing hazards affect the places, assets, ecosystems, and services communities value over 20-, 50-, and 100-year horizons?',
    'Which communities, places, and cultural, social, or environmental values are most at stake as conditions change?',
    'When might risks exceed acceptable or tolerable levels?',
    'Which adaptation options most effectively reduce risk, and how might strategies evolve as conditions change and new information emerges?',
  ],
  relatedUseCaseIds: [
    'emergency-planning-and-business-continuity',
    'asset-and-infrastructure-planning',
  ],
  forumUrl: 'https://forum.resilience-explorer.com/c/adaptation',
  overviewBody:
    'Adaptation planning requires managing how changing climate conditions affect the places, systems, and values that communities depend on. Sea-level rise, flooding, and extreme heat are physical processes that become risks only when they threaten homes, infrastructure, livelihoods, cultural heritage, and community wellbeing. Understanding those risks, how hazards interact with the places and values that matter, is both a technical and a governance challenge: risk information must be evidence-based and credible, but decisions about how to respond also depend on community values, long-term goals, and contested trade-offs that no model alone can resolve.',
  framingQuestion: 'How do we make robust adaptation decisions today when the future is uncertain?',
  learningPathwayIntro:
    'Adaptation planning spans decades and involves deep uncertainty. The articles below take you from foundational risk concepts through to methods for engaging communities, evaluating options, and meeting regulatory obligations.',
  tutorials: [
    {
      type: 'TUTORIAL',
      date: '09/10/25',
      title: 'Workspace Overview',
      description:
        'Learn how to organise hazard data, saved views, and workflows using Workspaces in Resilience Explorer.',
      thumbnail: '',
      href: '/app/guide/tutorials/organise-with-workspaces/workspaces-overview',
    },
    {
      type: 'DEMO',
      date: '09/10/25',
      title: 'Exploring long-term sea level rise scenarios',
      description:
        'Walk through how to compare sea level rise projections across emissions pathways and timeframes to inform adaptation planning decisions.',
      thumbnail: '/images/guide/demos/sea-level-rise-scenarios-thumbnail.png',
      href: '/app/guide/demos/sea-level-rise-scenarios',
    },
    {
      type: 'TUTORIAL',
      date: '09/10/25',
      title: 'Change how layers are displayed on the map',
      description:
        'Understand how to customise map layers and visualise hazard information more effectively during planning and response.',
      thumbnail: '',
      href: '/app/guide/tutorials/displaying-information-on-the-map/change-how-layers-are-displayed-on-the-map',
    },
    {
      type: 'TUTORIAL',
      date: '09/10/25',
      title: 'Filter results to an area of your choice',
      description:
        'Learn how to focus analysis on specific locations and operational areas using spatial filtering tools.',
      thumbnail: '',
      href: '/app/guide/tutorials/download-export-and-share-results/filter-results-to-an-area-of-your-choice',
    },
    {
      type: 'TUTORIAL',
      date: '09/10/25',
      title: 'Download an element report',
      description:
        'Generate downloadable reports for selected assets, infrastructure, and exposed elements.',
      thumbnail: '',
      href: '/app/guide/tutorials/download-export-and-share-results/download-an-element-report',
    },
  ],
  articleGroups: [
    {
      title: 'Foundational concepts',
      articles: [
        {
          title: 'What is Climate Change Adaptation',
          description:
            'What adaptation means, the four dimensions of adaptive action, the ARAP options framework, and why acting early keeps more options open.',
          href: '/app/guide/fundamentals/adaptation-and-system-change/what-is-climate-change-adaptation',
        },
        {
          title: 'What is risk',
          description:
            'Risk as consequence and uncertainty: the two-part definition that underpins every assessment in REx, and why values determine what counts as risk.',
          href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/what-is-risk',
        },
        {
          title: 'What are consequences',
          description:
            'The outcomes that matter in risk assessment: how consequences are defined, categorised, and connected to the things communities value.',
          href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/what-are-consequences',
        },
        {
          title: 'What is exposure',
          description:
            "What exposure means as a component of risk, and why mapping what is in harm's way is the starting point for any adaptation assessment.",
          href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/what-is-exposure',
        },
        {
          title: 'Climate Mitigation and Climate Adaptation',
          description:
            'Avoiding the unmanageable versus managing the unavoidable: what separates emissions reduction from climate risk management, and why both are necessary.',
          href: '/app/guide/fundamentals/climate-futures-and-scenarios/climate-mitigation-and-climate-adaptation',
        },
        {
          title: 'The Planning Process for Risk',
          description:
            'How to set up a risk assessment that will actually inform decisions: clarifying purpose, scope, methods, and audience before the analysis begins.',
          href: '/app/guide/fundamentals/planning-and-implementation/the-planning-process-for-risk',
        },
      ],
    },
    {
      title: 'Climate change and hazards',
      articles: [
        {
          title: 'Climate change: What you might need to think about',
          description:
            'A practical walkthrough of the MfE 10-step decision cycle, from understanding what is happening to monitoring and review, with guidance on what each phase requires.',
          href: '/app/guide/insights/commentary-and-opinion/climate-change-what-you-might-need-to-think-about',
        },
        {
          title: 'Sea Level Rise',
          description:
            'The causes, NZ-specific rates, and planning implications of sea-level rise, including why the NZ SeaRise tool and an increments-based approach suit adaptive planning better than fixed projections.',
          href: '/app/guide/fundamentals/hazards-and-stressors/sea-level-rise',
        },
        {
          title: 'Cascading Hazards',
          description:
            'How one hazard triggers the next, the mechanisms and infrastructure interdependencies involved, and why adaptation strategies need to account for these chains.',
          href: '/app/guide/fundamentals/hazards-and-stressors/cascading-hazards',
        },
      ],
    },
    {
      title: 'Adaptation options and pathways',
      articles: [
        {
          title: 'Dynamic Adaptive Pathways Planning (DAPP)',
          description:
            'How to build flexible adaptation strategies with signals and triggers that allow decisions to evolve as conditions change and new information emerges.',
          href: '/app/guide/fundamentals/decision-making-and-evaluation/dynamic-adaptive-pathways-planning',
        },
      ],
    },
    {
      title: 'Decision-making under uncertainty',
      articles: [
        {
          title: 'Robust Decision Making',
          description:
            'How to identify strategies that perform acceptably across a wide range of possible futures, not just the most likely one.',
          href: '/app/guide/fundamentals/decision-making-and-evaluation/robust-decision-making',
        },
        {
          title: 'Adaptive Capacity',
          description:
            'The economic, social, institutional, and environmental factors that determine whether people and systems can respond to climate hazards, and why building capacity matters as much as reducing exposure.',
          href: '/app/guide/fundamentals/adaptation-and-system-change/adaptive-capacity',
        },
        {
          title: 'Types of Uncertainty',
          description:
            'The conceptual framework for uncertainty in climate and risk contexts: epistemic versus aleatory uncertainty, deep uncertainty, and what each type means for how you plan.',
          href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/types-of-uncertainty',
        },
        {
          title: 'Sources of Uncertainty in Modelling',
          description:
            'Where modelling uncertainty comes from, what it means for your results, and how to ask better questions of technical experts without losing decision confidence.',
          href: '/app/guide/fundamentals/evidence-and-confidence/uncertainty-in-modelling',
        },
        {
          title: 'Heuristics and Cognitive Biases in Risk Decisions',
          description:
            'The mental shortcuts that help us act quickly but systematically distort risk judgements, and how to design decision-making processes that counteract them.',
          href: '/app/guide/fundamentals/decisions-and-behaviour/heuristics-and-cognitive-biases',
        },
        {
          title: 'Climate-Ready Infrastructure',
          description:
            'How infrastructure providers can move from single-scenario master planning to adaptive pathways that hold up under deep, compounding climate uncertainty, illustrated through the wastewater sector.',
          href: '/app/guide/insights/tools-and-practice/from-planning-to-action-a-practical-guide-for-climate-ready-infrastructure',
        },
      ],
    },
    {
      title: 'Equity, vulnerability, and communities',
      articles: [
        {
          title: 'Why Social Vulnerability Indicators Tell Only Half the Story',
          description:
            'Why composite vulnerability indices create convenient hotspots but miss lived complexity, and what a more complete, locally grounded understanding of vulnerability requires.',
          href: '/app/guide/insights/commentary-and-opinion/why-social-vulnerability-indicators-tell-only-half-the-story',
        },
        {
          title: 'Community values',
          description:
            'How to identify and structure what communities value as the foundation for risk assessment and adaptation planning, and why values must be explicit before priorities can be set.',
          href: '/app/guide/fundamentals/values-participation-and-governance/community-values-in-adaptation',
        },
        {
          title: 'Climate Justice in Adaptation',
          description:
            'How unequal exposure to climate risk reflects deeper structural inequities, and why justice considerations should shape who bears adaptation costs and who benefits.',
          href: '/app/guide/fundamentals/values-participation-and-governance/climate-justice-in-adaptation',
        },
      ],
    },
    {
      title: 'Planning process and regulation',
      articles: [
        {
          title: 'Relevant Legislation for Climate Risk Assessment',
          description:
            'The international agreements and NZ legislation that create adaptation obligations, from the Paris Agreement and Sendai Framework to the Climate Change Response Act and National Adaptation Plan.',
          href: '/app/guide/fundamentals/planning-and-implementation/relevant-legislation-for-climate-risk-assessment',
        },
        {
          title: 'Strength of Knowledge',
          description:
            'A scoring guide for assessing how well-founded your risk evidence is, and why documenting strength of knowledge is essential for credible, defensible adaptation decisions.',
          href: '/app/guide/fundamentals/evidence-and-confidence/strength-of-knowledge',
        },
      ],
    },
    {
      title: 'Community engagement',
      articles: [
        {
          title: 'Foundations of Risk Communication',
          description:
            'The principles that determine whether risk information leads to understanding and action: why technical accuracy is necessary but not sufficient for effective community engagement.',
          href: '/app/guide/fundamentals/decisions-and-behaviour/foundations-of-risk-communication',
        },
        {
          title: 'Community Engagement Principles',
          description:
            'Six principles that make engagement genuine rather than tokenistic, from timeliness and inclusivity to transparency and long-term institutional commitment.',
          href: '/app/guide/fundamentals/values-participation-and-governance/community-engagement-principles',
        },
        {
          title: 'Engagement with Māori',
          description:
            'How to engage with iwi, hapū, and whānau in ways that strengthen rather than undermine mana, the kaupapa Māori principles and practical approaches that make the difference.',
          href: '/app/guide/fundamentals/values-participation-and-governance/engagement-with-maori',
        },
        {
          title: 'Framing Effects in Risk Communication',
          description:
            'How the way risk and adaptation options are presented shapes what communities are willing to accept: why the same evidence can produce very different responses depending on how it is framed.',
          href: '/app/guide/fundamentals/decisions-and-behaviour/framing-effects-in-risk-communication',
        },
        {
          title: 'Marae-opoly: A serious game to support adaptation decision-making',
          description:
            "How a co-designed board game helped a Hawke's Bay hapū work through a century of adaptation decisions, and what serious games offer as a broader tool for community-led adaptation planning.",
          href: '/app/guide/insights/research-and-methods/marae-opoly-a-serious-game-to-support-adaptation-decision-making',
        },
        {
          title: 'Countering Science Denial',
          description:
            'How to maintain credible, evidence-based conversations with communities or decision-makers who are sceptical of climate projections or risk assessments.',
          href: '/app/guide/fundamentals/decisions-and-behaviour/countering-science-denial',
        },
      ],
    },
  ],
  dataNeedsIntro:
    'Different types of adaptation planning require different levels of data. The table below shows the datasets typically needed to move from identifying exposure, through to understanding impacts, prioritising risks, and testing long-term adaptation strategies.',
  capabilityLevels: [
    {
      level: 1,
      title: 'Identify exposure',
      description: 'Identify which places, systems, and communities are exposed to hazards',
    },
    {
      level: 2,
      title: 'Estimate impacts',
      description:
        'Estimate consequences, including built, infrastructure, social, economic, environmental, or cultural impacts',
    },
    {
      level: 3,
      title: 'Prioritise areas or risks',
      description: 'Identify where more detailed adaptation planning or action is most needed',
    },
    {
      level: 4,
      title: 'Develop adaptation strategies and pathways',
      description: 'Compare options, define thresholds, and sequence actions over time',
    },
  ],
  dataRequirements: [
    {
      dataset: 'Extent (e.g. flood extent)',
      category: 'Hazard',
      startLevel: 1,
    },
    {
      dataset: 'Intensity (e.g. flood depth, velocity, temperature, duration)',
      category: 'Hazard',
      startLevel: 2,
    },
    {
      dataset: 'Likelihood (e.g. return periods)',
      category: 'Hazard',
      startLevel: 2,
    },
    {
      dataset: 'Scenarios (e.g. global warming level, rainfall increase, sea-level rise)',
      category: 'Hazard',
      startLevel: 1,
    },
    {
      dataset: 'Buildings / development (e.g. residential, commercial, industrial)',
      category: 'Engineered elements',
      startLevel: 1,
    },
    {
      dataset:
        'Infrastructure and services (e.g. transport, water, wastewater, stormwater, energy)',
      category: 'Engineered elements',
      startLevel: 1,
    },
    {
      dataset: 'Land use / land cover (e.g. zoning, land cover, planning areas)',
      category: 'Spatial value layers',
      startLevel: 1,
    },
    {
      dataset: 'Environmental / ecological areas (e.g. wetlands, protected areas)',
      category: 'Spatial value layers',
      startLevel: 1,
    },
    {
      dataset:
        'Cultural / heritage / iwi datasets (e.g. heritage sites, culturally significant areas)',
      category: 'Spatial value layers',
      startLevel: 1,
    },
    {
      dataset: 'Natural features (e.g. rivers, wetlands)',
      category: 'Spatial value layers',
      startLevel: 1,
    },
    {
      dataset: 'Population and demographic data (e.g. census, deprivation, age profiles)',
      category: 'Inputs used to estimate consequences',
      startLevel: 2,
    },
    {
      dataset: 'Economic or value datasets (e.g. replacement cost, land value, GDP proxies)',
      category: 'Inputs used to estimate consequences',
      startLevel: 2,
    },
    {
      dataset:
        'Jurisdictional / reporting boundaries (e.g. electoral boundaries, catchments, census areas)',
      category: 'Planning and decision inputs',
      startLevel: 1,
    },
    {
      dataset:
        'Intervention options and associated hazard modelling (e.g. stopbanks, retreat areas, raised floor levels, infrastructure upgrades)',
      category: 'Planning and decision inputs',
      startLevel: 3,
    },
    {
      dataset:
        'Future / planning scenarios (e.g. growth areas, zoning changes, alternative spatial strategies)',
      category: 'Planning and decision inputs',
      startLevel: 4,
    },
    {
      dataset:
        'Risk thresholds and triggers (e.g. intolerable risk thresholds, sea-level thresholds, service outage thresholds)',
      category: 'Planning and decision inputs',
      startLevel: 4,
    },
  ],
}
